/**
 * Middle panel: chat assistant.
 *
 * Loads the active chat session from the backend, renders the
 * thread, and sends new operator turns to ``POST /v1/chat/sessions/
 * {id}/messages``. The composer is wired to the live Anthropic
 * Sonnet 4.6 backend when ``ANTHROPIC_API_KEY`` is configured on
 * the server (see ``scripts/dev_local.sh``); otherwise the server
 * returns a deterministic stub reply so the surface is exercisable
 * without a key.
 *
 * The thread is empty for a brand-new session; the suggested chips
 * remain visible so the operator can fire one-click prompts.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Icon } from "../icons";
import {
  createChatSession,
  getChatSession,
  loadConfig,
  sendChatMessageStream,
  retryChatMessageStream,
  type ApiError,
  type ChatKind,
  type ChatMessage,
  type ChatSession,
} from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../Toasts";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildTranscriptHtml(args: {
  contractor: string;
  contract: string;
  user: string;
  date: string;
  messages: ChatMessage[];
  renderBody: (text: string) => string;
}): string {
  const { contractor, contract, user, date, messages, renderBody } = args;
  const row = (label: string, value: string): string =>
    value
      ? `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`
      : "";
  const messageBlocks = messages
    .map((m) => {
      const author = m.role === "assistant" ? "Assistant" : user || "Operator";
      const time = (() => {
        try {
          return new Date(m.at).toLocaleString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        } catch {
          return "";
        }
      })();
      // renderBody returns trusted HTML produced by the same
      // sanitising markdown renderer the live chat uses; the popup
      // window is a same-origin blank doc, so the XSS surface is
      // identical to the in-app stream.
      return `
        <section class="msg msg-${escapeHtml(m.role)}">
          <header><span class="who">${escapeHtml(author)}</span><span class="when">${escapeHtml(time)}</span></header>
          <div class="body">${renderBody(m.content)}</div>
        </section>`;
    })
    .join("");
  // Inline styles only; the popup is opened with about:blank so it
  // cannot reach the SPA's stylesheet. Keep the layout printer-
  // friendly: A4 page, 25mm margins, 11pt body, no fancy fonts.
  return `<!doctype html>
<html lang="en-AU">
<head>
<meta charset="utf-8" />
<title>Chat transcript${contract ? ` — ${escapeHtml(contract)}` : ""}</title>
<style>
  @page { size: A4; margin: 25mm; }
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    color: #000;
    font-size: 11pt;
    line-height: 1.5;
    margin: 0;
    padding: 24px;
  }
  .title-box {
    border: 1px solid #000;
    padding: 14px 18px;
    margin-bottom: 24px;
  }
  .title-box h1 {
    margin: 0 0 10px;
    font-size: 16pt;
    font-weight: 600;
  }
  .title-box table { border-collapse: collapse; }
  .title-box th {
    text-align: left;
    font-weight: 600;
    padding: 2px 14px 2px 0;
    vertical-align: top;
    width: 110px;
  }
  .title-box td { padding: 2px 0; vertical-align: top; }
  .msg {
    margin-bottom: 16px;
    page-break-inside: avoid;
  }
  .msg header {
    margin-bottom: 4px;
    font-size: 10.5pt;
  }
  .msg .who { font-weight: 600; }
  .msg .when { color: #555; margin-left: 10px; font-size: 9.5pt; }
  .msg .body p { margin: 0 0 8px; }
  .msg .body ul, .msg .body ol { margin: 4px 0 8px 22px; padding: 0; }
  .msg .body li { margin: 2px 0; }
  .msg .body code {
    font-family: "SF Mono", ui-monospace, Menlo, monospace;
    font-size: 10pt;
    background: #f4f4f4;
    padding: 1px 4px;
    border-radius: 3px;
  }
  .msg .body pre {
    background: #f4f4f4;
    padding: 8px 10px;
    border-radius: 4px;
    overflow-x: auto;
    font-size: 9.5pt;
  }
  .msg .body h1 { font-size: 13pt; margin: 8px 0 4px; }
  .msg .body h2 { font-size: 12pt; margin: 8px 0 4px; }
  .msg .body h3 { font-size: 11pt; margin: 6px 0 4px; }
  .msg-user .body {
    background: #f1f5fb;
    border-left: 3px solid #0a3a6b;
    padding: 8px 12px;
  }
  .msg-assistant .body {
    background: #ffffff;
    border-left: 3px solid #444;
    padding: 8px 12px;
  }
  @media print {
    body { padding: 0; }
  }
</style>
</head>
<body>
  <div class="title-box">
    <h1>Chat transcript</h1>
    <table>
      <tbody>
        ${row("Contractor", contractor)}
        ${row("Contract", contract)}
        ${row("User", user)}
        ${row("Date", date)}
      </tbody>
    </table>
  </div>
  <div class="thread">
    ${messageBlocks || '<p style="color:#666">No messages in this conversation.</p>'}
  </div>
</body>
</html>`;
}

function operatorInitials(label: string | null | undefined): string {
  if (!label) {
    return "?";
  }
  const local = label.split("@", 1)[0];
  const tokens = local.split(/[\s._\-]+/).filter(Boolean);
  if (tokens.length === 0) {
    return "?";
  }
  if (tokens.length === 1) {
    return tokens[0].slice(0, 2).toUpperCase();
  }
  return (tokens[0][0] + tokens[1][0]).toUpperCase();
}

const SUGGESTIONS = [
  "Identify the basis clause for this variation",
  "Test notice compliance",
  "Re-price at the Schedule of Rates",
  "Draft the determination letter",
];

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch {
    return "";
  }
}

// Hand-rolled markdown renderer for assistant messages. The
// platform deliberately ships without a third-party markdown
// library; the assistant emits a small, predictable subset and we
// render that subset safely without unrelated transitive deps.
//
// Supported syntax:
//   - ATX headers (# / ## / ###)
//   - **bold**, *italic*, `inline code`
//   - Fenced code blocks (```...```)
//   - Bullet lists (- foo / * foo)
//   - Numbered lists (1. foo / 2. foo)
//   - Inline links [text](url) — http/https/mailto only
//   - Blank-line paragraph separation
//
// Everything else is rendered as plain text. Every character that
// is not part of an explicitly-emitted tag is HTML-escaped, so the
// output is XSS-safe under dangerouslySetInnerHTML. The link
// scheme allowlist defends against javascript: URIs in particular.
function renderMessageHtml(text: string): { __html: string } {
  const escape = (s: string): string =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  const sanitiseHref = (raw: string): string | null => {
    const trimmed = raw.trim();
    if (/^(https?:|mailto:)/i.test(trimmed)) {
      return escape(trimmed);
    }
    return null;
  };
  // Inline transformations run on already-escaped text. Order
  // matters: code first (so its contents are not further
  // formatted), then links (so their text can carry bold/italic
  // afterwards), then bold, then italic.
  const inline = (s: string): string => {
    let out = escape(s);
    out = out.replace(/`([^`]+)`/g, "<code>$1</code>");
    out = out.replace(
      /\[([^\]]+)\]\(([^)\s]+)\)/g,
      (match, label: string, url: string) => {
        const safe = sanitiseHref(url);
        if (safe === null) {
          return match;
        }
        return `<a href="${safe}" rel="noopener noreferrer" target="_blank">${label}</a>`;
      },
    );
    out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    out = out.replace(
      /(^|[\s(])\*([^*\s][^*]*)\*/g,
      (_m, lead: string, body: string) => `${lead}<em>${body}</em>`,
    );
    return out;
  };
  const lines = text.split(/\r?\n/);
  const blocks: string[] = [];
  type ListKind = "ul" | "ol";
  let listKind: ListKind | null = null;
  let para: string[] = [];
  let codeOpen = false;
  let codeBuf: string[] = [];
  const flushPara = (): void => {
    if (para.length === 0) return;
    blocks.push(`<p>${para.map(inline).join("<br>")}</p>`);
    para = [];
  };
  const flushList = (): void => {
    if (listKind !== null) {
      blocks.push(`</${listKind}>`);
      listKind = null;
    }
  };
  const openList = (kind: ListKind): void => {
    if (listKind === kind) return;
    flushList();
    blocks.push(`<${kind}>`);
    listKind = kind;
  };
  for (const raw of lines) {
    // A fenced code block swallows every line, including blanks
    // and what would otherwise be other markdown, until its
    // closing fence.
    if (codeOpen) {
      if (/^\s*```\s*$/.test(raw)) {
        blocks.push(`<pre><code>${escape(codeBuf.join("\n"))}</code></pre>`);
        codeBuf = [];
        codeOpen = false;
      } else {
        codeBuf.push(raw);
      }
      continue;
    }
    if (/^\s*```/.test(raw)) {
      flushPara();
      flushList();
      codeOpen = true;
      continue;
    }
    const line = raw.trimEnd();
    const header = /^(#{1,3})\s+(.*)$/.exec(line);
    if (header) {
      flushPara();
      flushList();
      const level = header[1].length;
      blocks.push(`<h${level}>${inline(header[2] ?? "")}</h${level}>`);
      continue;
    }
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      flushPara();
      openList("ul");
      blocks.push(`<li>${inline(bullet[1] ?? "")}</li>`);
      continue;
    }
    const numbered = /^\s*\d+\.\s+(.*)$/.exec(line);
    if (numbered) {
      flushPara();
      openList("ol");
      blocks.push(`<li>${inline(numbered[1] ?? "")}</li>`);
      continue;
    }
    if (line === "") {
      flushPara();
      flushList();
      continue;
    }
    if (listKind !== null) {
      flushList();
    }
    para.push(line);
  }
  if (codeOpen) {
    // Defensive: an unterminated fence still renders as a code
    // block rather than swallowing the rest of the message.
    blocks.push(`<pre><code>${escape(codeBuf.join("\n"))}</code></pre>`);
  }
  flushPara();
  flushList();
  return { __html: blocks.join("") };
}

// Shape of an auto-created chat session. Used when the operator
// types into the composer without first clicking + New chat;
// AssistantPanel calls the parent's getDefaultSessionPayload so
// the auto-created session inherits whatever scoping the parent
// surface implies (contractor + contract on Tab 2; no scope on
// Workspace). The parent must also stamp the right `kind`: Tab 2
// supplies kind:"contractor" so the chat is filed on the Tab-2
// list, otherwise it routes to the Workspace list (ADR-0015). The
// visible symptom of getting this wrong is "I typed a message and
// the chat ended up on the other tab's list".
export interface AutoSessionPayload {
  title?: string;
  variation_label?: string;
  variation_brief?: Record<string, unknown>;
  assessment_id?: string | null;
  // Surface tag carried through to createChatSession so an
  // auto-created session is filed on the right list (Tab 2 supplies
  // "contractor"; the Workspace default is "workspace"). See ADR-0015.
  kind?: ChatKind;
}

export function AssistantPanel({
  sessionId,
  onMessageSent,
  onSessionMissing,
  onSessionCreated,
  onSessionAssessmentLinked,
  getDefaultSessionPayload,
  refreshSignal = 0,
}: {
  sessionId: string | null;
  onMessageSent: () => void;
  onSessionMissing: () => void;
  onSessionCreated: (sessionId: string) => void;
  onSessionAssessmentLinked: (assessmentId: string | null) => void;
  getDefaultSessionPayload?: () => AutoSessionPayload;
  // Bumped by the parent when the active session is re-pointed in
  // place (e.g. "Link to Assistant" relinks the same session id to a
  // new variation). The load effect keys on it as well as sessionId,
  // so the panel re-reads the session and the binding chip updates
  // even though the session id did not change. See ADR-0015.
  refreshSignal?: number;
}) {
  const toast = useToast();
  const { principal } = useAuth();
  const operatorLabel = useMemo(() => {
    if (principal?.display_name) return principal.display_name;
    if (principal?.email) return principal.email.split("@", 1)[0];
    return "You";
  }, [principal?.display_name, principal?.email]);
  const operatorAvatar = useMemo(
    () => operatorInitials(principal?.display_name ?? principal?.email ?? null),
    [principal?.display_name, principal?.email],
  );
  const [session, setSession] = useState<ChatSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState(false);
  // Partial assistant text streamed token-by-token (ADR-0022). Non-null
  // while a streamed reply is in flight; the live bubble renders this
  // with a typing indicator. Cleared when the terminal ``done`` event
  // lands and the authoritative session (carrying the finished
  // assistant message) replaces it, so the text is never shown twice.
  // It stays null on the flag-off buffered path, which paints nothing
  // until the reply resolves and the existing typing-dots row covers
  // the wait.
  const [streamingText, setStreamingText] = useState<string | null>(null);
  // True after a send/retry fails, so the composer can offer a one-click
  // "Retry last message" (the failed turn is persisted server-side, so a
  // retry needs no retyping). Cleared on the next attempt and on success.
  const [failed, setFailed] = useState(false);
  const streamRef = useRef<HTMLDivElement | null>(null);

  // Load the session whenever the active id changes.
  useEffect(() => {
    if (sessionId === null) {
      setSession(null);
      onSessionAssessmentLinked(null);
      return;
    }
    let cancelled = false;
    const cfg = loadConfig();
    setLoading(true);
    getChatSession(cfg, sessionId)
      .then((s) => {
        if (cancelled) return;
        setSession(s);
        // Surface the linked assessment_id so the variation panel
        // can pull the determination as part of the 'resume work'
        // flow.
        onSessionAssessmentLinked(s.assessment_id ?? null);
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        setSession(null);
        onSessionAssessmentLinked(null);
        // 404 means the stored session id no longer exists in the
        // backend (process restart wipes the in-memory store). Clear
        // it silently rather than spamming an error toast.
        if (err.status === 404) {
          onSessionMissing();
          return;
        }
        toast.show("err", `Load failed: ${err.message ?? "unknown"}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, refreshSignal, toast, onSessionMissing, onSessionAssessmentLinked]);

  // Scroll to bottom on message change and as streamed text grows, so
  // the newest tokens stay in view while the reply is being written.
  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [session?.messages.length, streamingText]);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      setPending(true);
      setFailed(false);
      // Start each send with no partial; deltas accumulate here as they
      // stream in (ADR-0022). On the flag-off buffered path no deltas
      // arrive and this stays null, so the typing-dots row shows instead.
      setStreamingText(null);
      // Clear the composer immediately so the next keystroke
      // doesn't fight against in-flight state.
      setDraft("");
      const cfg = loadConfig();
      // Optimistic append: the operator sees their message land
      // in the stream as soon as they hit send, without waiting
      // for the model. The typing-dots row already covers the
      // assistant-half of the round-trip. On success we replace
      // the optimistic state with the server's authoritative
      // session (which carries server-stamped timestamps and the
      // assistant reply); on failure we drop the optimistic
      // message and surface the error.
      const optimisticMsg: ChatMessage = {
        role: "user",
        content: trimmed,
        at: new Date().toISOString(),
        input_tokens: 0,
        output_tokens: 0,
        citations: [],
      };
      // When there is no active session yet (operator types into the
      // composer with no chat open), seed a placeholder session so the
      // optimistic bubble lands in the stream before the
      // createChatSession round-trip. flushSync is the load-bearing
      // bit: React 18's automatic batching can hold the setState in
      // its scheduler queue across the await on createChatSession /
      // sendChatMessage, so the bubble does not paint until the
      // server reply lands. flushSync forces the commit to happen
      // synchronously here, before any network I/O begins, which is
      // exactly what an optimistic update needs.
      flushSync(() => {
        setSession((prev) => {
          if (prev !== null) {
            return { ...prev, messages: [...prev.messages, optimisticMsg] };
          }
          const now = optimisticMsg.at;
          return {
            session_id: "",
            tenant_id: "",
            title: "New chat",
            variation_label: "",
            variation_brief: {},
            assessment_id: null,
            kind: "workspace",
            created_at: now,
            updated_at: now,
            messages: [optimisticMsg],
          };
        });
      });
      try {
        let activeId = sessionId;
        if (!activeId) {
          // No active session yet, so open one so the operator does
          // not have to click + New chat before typing. The
          // creation payload comes from the parent surface so the
          // session inherits the right scoping and kind (Tab 2
          // supplies contractor + contract + contract_id and
          // kind:"contractor"; Workspace returns the generic default
          // below, which is kind:"workspace"). Without that hand-off,
          // a Tab-2 auto-create would be filed on the Workspace list
          // by the kind filter (ADR-0015), which flips sessionId
          // mid-send and the optimistic bubble disappears.
          const payload = getDefaultSessionPayload?.() ?? {
            title: "General chat",
            variation_label: "",
          };
          const created = await createChatSession(cfg, payload);
          activeId = created.session_id;
          // Carry the optimistic message into the freshly-created
          // session so it doesn't disappear between the create
          // and send round-trips.
          setSession({ ...created, messages: [...created.messages, optimisticMsg] });
          onSessionCreated(activeId);
        }
        const result = await sendChatMessageStream(cfg, activeId, trimmed, {
          // Each token chunk extends the live partial. The functional
          // updater keeps appends correct under React 18 batching when
          // many deltas land in one frame.
          onDelta: (text) => setStreamingText((prev) => (prev ?? "") + text),
        });
        // The authoritative session carries the finished assistant
        // message and merged citations; swap to it and drop the partial
        // in the same commit so the text never flashes twice.
        flushSync(() => {
          setSession(result.session);
          setStreamingText(null);
        });
        onMessageSent();
      } catch (err) {
        // Leave the optimistic bubble in place on failure. Removing
        // it produced the "flashes up then gone" behaviour the
        // operator was reporting when the upstream timed out: the
        // bubble appeared, sat with the typing dots for ~60s, then
        // vanished with a transport error. Keeping the bubble lets
        // the operator see what they typed and re-send, and the
        // toast surfaces the actual failure.
        const apiErr = err as ApiError;
        setFailed(true);
        toast.show("err", `Send failed: ${apiErr.message ?? "unknown"}`);
      } finally {
        setPending(false);
        // Drop any partial on failure so a half-streamed reply is not
        // left frozen below the optimistic bubble; on success the
        // done-event handler already cleared it. The persisted user
        // turn lets the operator retry without retyping.
        setStreamingText(null);
      }
    },
    [sessionId, onMessageSent, onSessionCreated, getDefaultSessionPayload, toast],
  );

  // Re-run the last unanswered message without retyping. The failed turn
  // is already persisted, so the server re-runs it (no duplicate). Best
  // for a transient failure like a 529 overload after a short wait.
  const retry = useCallback(async () => {
    const id = session?.session_id;
    if (!id) return;
    setPending(true);
    setFailed(false);
    setStreamingText(null);
    const cfg = loadConfig();
    try {
      const result = await retryChatMessageStream(cfg, id, {
        onDelta: (text) => setStreamingText((prev) => (prev ?? "") + text),
      });
      flushSync(() => {
        setSession(result.session);
        setStreamingText(null);
      });
      onMessageSent();
    } catch (err) {
      const apiErr = err as ApiError;
      setFailed(true);
      toast.show("err", `Retry failed: ${apiErr.message ?? "unknown"}`);
    } finally {
      setPending(false);
      setStreamingText(null);
    }
  }, [session?.session_id, onMessageSent, toast]);

  const messageCount = session?.messages.length ?? 0;
  const exportPdf = useCallback(() => {
    if (session === null || session.messages.length === 0) {
      toast.show("err", "Nothing to export yet — send a message first.");
      return;
    }
    const brief = (session.variation_brief ?? {}) as Record<string, unknown>;
    const briefStr = (key: string): string => {
      const raw = brief[key];
      return typeof raw === "string" ? raw : "";
    };
    const contractor = briefStr("contractor");
    const contractRef = briefStr("contract_reference") || session.variation_label || "";
    const contractName = briefStr("contract_name");
    const contractLabel = [contractRef, contractName].filter(Boolean).join(" — ");
    const date = new Date().toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    const html = buildTranscriptHtml({
      contractor,
      contract: contractLabel,
      user: operatorLabel,
      date,
      messages: session.messages,
      renderBody: (text) => renderMessageHtml(text).__html,
    });
    // Hidden iframe rather than window.open: pop-up blockers refuse
    // most window.open calls except in narrow synchronous-user-
    // gesture cases, and the "noopener" feature returns null in
    // Chrome which is exactly the false-positive the operator was
    // hitting. Printing from an iframe stays inside the SPA and
    // never trips the pop-up gate.
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.opacity = "0";
    document.body.appendChild(iframe);
    const win = iframe.contentWindow;
    const doc = iframe.contentDocument ?? win?.document ?? null;
    if (win === null || doc === null) {
      iframe.remove();
      toast.show("err", "Could not start print preview.");
      return;
    }
    doc.open();
    doc.write(html);
    doc.close();
    // afterprint fires when the operator closes the print dialog
    // (saved or cancelled). Some browsers do not dispatch it; the
    // 60-second fallback removes the iframe so the DOM does not
    // accumulate hidden frames over a long session.
    const cleanup = (): void => {
      iframe.remove();
    };
    win.addEventListener("afterprint", cleanup, { once: true });
    window.setTimeout(cleanup, 60_000);
    // Defer the print dialog until layout is settled; some browsers
    // race the document.write and produce a blank preview otherwise.
    const trigger = (): void => {
      try {
        win.focus();
        win.print();
      } catch {
        cleanup();
        toast.show("err", "Could not open the print dialog.");
      }
    };
    if (doc.readyState === "complete") {
      window.setTimeout(trigger, 150);
    } else {
      win.addEventListener("load", () => window.setTimeout(trigger, 150), {
        once: true,
      });
    }
  }, [session, operatorLabel, toast]);

  // Which variation this chat is bound to, surfaced as a persistent
  // chip so the operator is never guessing what "the variation" means
  // to the assistant. A determination on file is named as such; a
  // chat re-pointed at a freshly uploaded variation (no run yet) is
  // flagged preliminary; a chat with no variation at all says so
  // plainly rather than implying a binding that does not exist. This
  // is the visible half of the no-silent-determination guard (the
  // enforced half lives server-side in render_system_prompt). See
  // ADR-0015.
  const binding = useMemo(() => {
    if (session === null) return null;
    const brief = (session.variation_brief ?? {}) as Record<string, unknown>;
    const briefVid = typeof brief.variation_id === "string" ? brief.variation_id : "";
    const label = session.variation_label || briefVid;
    const hasDetermination = session.assessment_id !== null;
    if (!label && !hasDetermination) {
      return { label: "", state: "general" as const };
    }
    return {
      label,
      state: hasDetermination ? ("determination" as const) : ("preliminary" as const),
    };
  }, [session]);

  return (
    <section className="col col-chat col-assistant" aria-label="Assistant">
      <div className="col-head">
        <div className="col-title">ASSISTANT</div>
        <div className="col-head-actions">
          <button
            type="button"
            className="btn btn-primary btn-sm btn-export-pdf"
            onClick={exportPdf}
            disabled={messageCount === 0}
            title={
              messageCount === 0
                ? "No messages to export yet"
                : "Open the chat as a printable PDF"
            }
          >
            PDF
          </button>
          <span className="status-online">
            <Icon.online />
            Online
          </span>
        </div>
      </div>

      {binding && (
        <div
          className={`assistant-binding assistant-binding-${binding.state}`}
          data-testid="assistant-binding"
        >
          {binding.state === "general" ? (
            <span className="assistant-binding-text">
              No variation linked. Answers are general.
            </span>
          ) : (
            <span className="assistant-binding-text">
              <span className="assistant-binding-label">
                {binding.label ? `Variation: ${binding.label}` : "Variation linked"}
              </span>
              <span className="assistant-binding-state">
                {binding.state === "determination"
                  ? "determination on file"
                  : "preliminary, no determination run yet"}
              </span>
            </span>
          )}
        </div>
      )}

      <div ref={streamRef} className="stream stream-chat" data-testid="assistant-stream">
        {sessionId !== null && loading && session === null && (
          <div className="empty empty-compact">
            <p>Loading session…</p>
          </div>
        )}
        {session !== null && session.messages.length === 0 && (
          <div className="empty empty-compact">
            <h3>{session.title}</h3>
            <p>Ask about a variation, request a draft, or pick a suggested prompt below.</p>
          </div>
        )}
        {session !== null &&
          session.messages.map((m: ChatMessage, i: number) => (
            <div key={i} className={`msg msg-${m.role}`}>
              <div className="msg-head">
                <div className={`msg-av ${m.role === "assistant" ? "ai" : ""}`}>
                  {m.role === "assistant" ? "AI" : operatorAvatar}
                </div>
                <div className="msg-author">{m.role === "assistant" ? "Assistant" : operatorLabel}</div>
                <div className="msg-time">{formatTime(m.at)}</div>
              </div>
              <div
                className={`msg-body ${m.role === "assistant" ? "msg-body-md" : ""}`}
                dangerouslySetInnerHTML={renderMessageHtml(m.content)}
              />
              {/* Citations and other structured output belong in the
                  Determination panel; the chat is conversational
                  only. The retrieval the assistant does to ground
                  its replies stays server-side. */}
            </div>
          ))}
        {pending && (
          <div className="msg msg-assistant">
            <div className="msg-head">
              <div className="msg-av ai">AI</div>
              <div className="msg-author">Assistant</div>
              <div className="msg-time">…</div>
            </div>
            {streamingText !== null && streamingText.length > 0 ? (
              // Streamed partial (ADR-0022): render the tokens received
              // so far through the same sanitising markdown renderer the
              // finished message uses, with a trailing typing indicator
              // so it reads as in-progress. On the terminal done event
              // the authoritative message replaces this in one commit.
              <div
                className="msg-body msg-body-md msg-body-streaming"
                data-testid="assistant-streaming"
              >
                <span dangerouslySetInnerHTML={renderMessageHtml(streamingText)} />
                <span className="typing typing-inline" aria-hidden="true">
                  <span /> <span /> <span />
                </span>
              </div>
            ) : (
              // No token has arrived yet (still waiting on first byte),
              // or the buffered flag-off path: show the typing dots.
              <div className="msg-body">
                <span className="typing">
                  <span /> <span /> <span />
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="suggestions">
        <div className="suggestions-label">SUGGESTED</div>
        <div className="suggestions-row">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              className="chip-suggest"
              disabled={pending}
              onClick={() => void send(s)}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {failed && !pending && (
        <div className="suggestions">
          <div className="suggestions-row">
            <button
              type="button"
              className="chip-suggest"
              onClick={() => void retry()}
            >
              Retry last message
            </button>
          </div>
        </div>
      )}
      <form
        className="composer"
        onSubmit={(e) => {
          e.preventDefault();
          void send(draft);
        }}
      >
        <button type="button" className="btn-icon composer-attach" aria-label="Attach">
          <Icon.attachment />
        </button>
        <input
          type="text"
          placeholder={
            sessionId
              ? "Ask about this variation, or request a draft…"
              : "Type a message - a chat will start automatically."
          }
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={pending}
        />
        <button
          type="submit"
          className="btn-icon composer-send"
          aria-label="Send"
          disabled={pending || draft.trim().length === 0}
        >
          <Icon.send />
        </button>
      </form>
    </section>
  );
}
